import { useLayoutEffect, useRef, useState } from 'react';
import {
  EAC_MODES,
  forecastHours,
  forecastLines,
  type ContractFinance,
  type EacMode,
  type FinanceBucket,
  type FinanceGranularity,
} from '@forecastify/shared';
import { Button, DropdownMenu, Icon, Segmented, clampInto, useIsMobile } from '@forecastify/ui';
import { useI18n, useT, type Locale } from '../../../i18n';
import { eacLabel } from '../eac-mode';
import { compactMoney, money, signedMoney, useHourFormat, type Currency } from './finance-format';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C58 · BÜDZSÉ ÉS FELHASZNÁLÁS — bars are hours, lines are money.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ U-2 · ONE AXIS. AN HOUR IS DRAWN AT PLANNED COST ÷ PLANNED HOURS.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The columns are HOURS and the lines are MONEY, and they used to sit on two unrelated scales — so
 * a column's height said nothing about the line beside it, and the hour axis was stretched to end
 * just under the budget rule (`hmax = maxH / (bf * 0.92)`) so that no column could ever cross it.
 * Now one hour is drawn at the server's `hourRate`: a planned column's top meets the planned-cost
 * line, and a column over the budget rule IS hours the budget does not pay for. The right-hand
 * labels are the left-hand ones divided by that rate, so the two tick sets mark the same heights.
 *
 * ⚠️ **No rate at all** (nothing planned, nothing logged with a cost) keeps two independent axes,
 * because an invented rate would draw a relationship that does not exist.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ FOUR THINGS WERE WRONG AT ONCE AND THREE OF THEM WERE ARITHMETIC.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Read against the mockup's own chart (`fcd.chart`) rather than against a screenshot of it:
 *
 *  1. **THE BUDGET LINE CLIMBED.** It is a FIXED total — `budV.push(budTotW)`, the same figure in
 *     every bucket — because the question the chart answers is *does the running spend cross the
 *     budget*, and a budget that grows with the spend can never be crossed. Ours accumulated it
 *     bucket by bucket, which drew the one line on the chart that cannot legitimately slope.
 *  2. **THE PILL NAMING IT WAS MISSING.** The mockup labels the line where it meets the left edge,
 *     because a flat red rule with nothing on it is a decoration.
 *  3. **THE TWO BARS SAT SIDE BY SIDE.** The mockup nests them: planned is the WIDE one behind
 *     (`inset-x-[10%]`), logged the narrow one in FRONT (`inset-x-[27%]`), both from the same
 *     baseline — so the overhang IS the gap between plan and reality, read without arithmetic.
 *     Side by side, the same two numbers say nothing about each other.
 *  4. **PERIOD/CUMULATIVE DROVE THE WRONG SERIES.** It toggled the money lines and left the
 *     columns raw — exactly backwards. The mockup: *"a pénzvonalak mindig kumuláltak — a
 *     Periódus/Kumulált csak az óra-oszlopokra hat."* The lines are cumulative by nature (a running
 *     total against a fixed budget); the switch is about the COLUMNS, and cumulative there means
 *     this bucket plus every bucket before it.
 *
 * ⚠️ **The scale is always computed from the CUMULATIVE totals**, so switching Period/Cumulative
 * moves the bars without moving the ruler.
 *
 * ⚠️ **No legend row.** The mockup computes one and draws it nowhere; the pill names the budget
 * line and the hover card names every series with its own swatch. One of the 140 keys in the
 * mockup's logic that the mockup itself renders with nothing.
 *
 * ⚠️ **CSS bars and one inline SVG**, like `hours-chart.tsx` — no charting library. The shapes are
 * a bar and a line, and a dependency that draws them would bring its own fonts, colours and
 * tooltips to fight with the design system.
 */

const COLOR = {
  logged: 'var(--primary)',
  planned: 'color-mix(in oklch, var(--primary) 20%, transparent)',
  budget: 'var(--destructive)',
  cost: 'var(--status-ok)',
  revenue: 'var(--chart-revenue)',
  margin: 'var(--success)',
} as const;

/**
 * ⚠️ **The effort chart's own hatch, at each bar's own weight.** 45°, 3px on 3px off — the same
 * texture on both tabs, so *this is a projection* reads the same way wherever somebody meets it.
 */
const HATCH_LOGGED =
  'repeating-linear-gradient(45deg, color-mix(in oklch, var(--primary) 55%, transparent) 0 3px, transparent 3px 6px)';
const HATCH_PLANNED =
  'repeating-linear-gradient(45deg, color-mix(in oklch, var(--primary) 20%, transparent) 0 3px, transparent 3px 6px)';

const clamp = (value: number): number => Math.max(0, Math.min(100, value));
const pct = (value: number): string => `${value.toFixed(2)}%`;

/** The running total of a series — the shape the lines always have and the columns have on demand. */
function total(values: readonly number[]): number[] {
  let sum = 0;
  return values.map((value) => (sum += value));
}

interface Segment {
  readonly key: string;
  readonly points: string;
  readonly color: string;
  readonly dash: string;
}

/** EV-10 · the five forecasts' lowest and highest running total at each column. */
interface Range {
  readonly low: readonly number[];
  readonly high: readonly number[];
}

export function FinanceChart({
  view,
  currency,
  eacMode,
  granularity,
  onGranularity,
  internal = false,
  noBudget = false,
}: {
  readonly view: ContractFinance;
  readonly currency: Currency;
  /**
   * 30 Oct 2026 · every line item in view is time-and-material, so there is no budget to draw:
   * profitability only, and no switch. `internal` wins — an internal project earns nothing.
   */
  readonly noBudget?: boolean;
  /** 21 Oct 2026 · an internal project earns nothing: budget view only, no profit switch. */
  readonly internal?: boolean;
  /** The selected EAC — the mode every line on the chart follows (EV-10). */
  readonly eacMode: EacMode;
  /**
   * 16 Oct 2026 · *Nap / Hét / Hónap*. ⚠️ `null` on a recurring contract's own view, whose columns
   * are its periods — the mockup offers no switch there, and neither does this.
   */
  readonly granularity: FinanceGranularity | null;
  readonly onGranularity: (value: FinanceGranularity) => void;
}) {
  const { hours } = useHourFormat();
  const mobile = useIsMobile();
  const t = useT();
  const { locale } = useI18n();
  const [chosenMode, setMode] = useState<'budget' | 'profit'>('budget');
  const mode = internal ? 'budget' : noBudget ? 'profit' : chosenMode;
  const [shape, setShape] = useState<'period' | 'cumulative' | null>(null);
  const [hovered, setHovered] = useState<number | null>(null);
  /* ⚠️ 3 Nov 2026 · the hover tip is kept inside the WINDOW, measured after layout: a tall one on
     a chart near the foot of the screen ran off its bottom. */
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [nudge, setNudge] = useState({ x: 0, y: 0 });
  useLayoutEffect(() => {
    const node = tipRef.current;
    if (node === null) return;
    const next = clampInto(node.getBoundingClientRect(), nudge);
    if (next.x !== nudge.x || next.y !== nudge.y) setNudge(next);
  }, [hovered, nudge]);

  /* ⚠️ `null` means *the reader has not chosen*, so the default follows the mode — and an explicit
     choice survives switching back and forth, which a plain default would not. */
  const cumulative = (shape ?? (mode === 'budget' ? 'cumulative' : 'period')) === 'cumulative';

  const buckets = view.buckets;
  if (buckets.length === 0) {
    return (
      <section className="bg-card rounded-xl border p-4 shadow-sm">
        <p className="text-muted-foreground text-center text-[12px]">{t('finance.chart.empty')}</p>
      </section>
    );
  }

  const count = buckets.length;
  /**
   * ⚠️ **`solidTo` is the last bucket that has HAPPENED**, and it does four jobs: the money lines
   * run solid up to it and dashed after (measured, then forecast), the marker sits at its centre,
   * its label is the one drawn in weight, and the buckets past it are shaded. `future` is the only
   * flag the wire carries, so all four read one boundary and cannot disagree about where now is.
   */
  const firstFuture = buckets.findIndex((bucket) => bucket.future);
  const solidTo = firstFuture < 0 ? count - 1 : Math.max(0, firstFuture - 1);
  /* A marker at an edge says nothing: every period past, or every one still ahead. */
  const currentAt = firstFuture > 0 && firstFuture < count ? ((solidTo + 0.5) / count) * 100 : null;

  /* ── the columns: HOURS, and the only series the Period/Cumulative switch touches ──────────── */
  const loggedRaw = buckets.map((bucket) => Number(bucket.loggedHours));
  const plannedRaw = buckets.map((bucket) => Number(bucket.plannedHours));
  const loggedBars = cumulative ? total(loggedRaw) : loggedRaw;
  const plannedBars = cumulative ? total(plannedRaw) : plannedRaw;
  /**
   * ⚠️⚠️ 2 Nov 2026 · **THE NARROW COLUMN AHEAD IS THE SELECTED MODE'S HOURS, NOT THE LOGGED TOTAL
   * REPEATED.** Nobody logs next month, so a future column's logged hours are 0 and the cumulative
   * one used to stand at today's total in every column after it — hatched, so it read as a
   * forecast, and flat while the dashed spend line beside it climbed. It is now logged plus the
   * hours the mode's walk spends there (`ahead[mode].hours`), so it rises with that line and stops
   * where the mode finishes. The current column draws its logged part solid, and what is still
   * walked in it hatched above.
   */
  const forecastSeries = forecastHours(buckets, eacMode);
  const forecastBars = cumulative ? forecastSeries.cumulative : forecastSeries.perPeriod;
  const walkedIn = buckets.map((bucket) => Number(bucket.ahead[eacMode].hours) > 0.004);
  /* ⚠️ Always the cumulative maximum, so the right-hand ruler does not jump when the switch does. */
  const maxHours = Math.max(
    1,
    ...total(loggedRaw),
    ...total(plannedRaw),
    ...forecastSeries.cumulative,
  );

  /* ── the lines: MONEY, always cumulative, against a budget that never moves ────────────────── */
  /* ⚠️ The SCOPE's budget, not the sum of the drawn buckets — `budTot = BACm > 0.4 ? BACm : Σ`.
     A horizon that shows twelve of twenty periods must still measure against the whole budget, or
     the line moves when the reader changes the window. The sum is the fallback for a scope that
     carries no figure of its own. */
  const budgetTotal =
    Number(view.budget) > 0.4
      ? Number(view.budget)
      : buckets.reduce((sum, bucket) => sum + Number(bucket.budget), 0);
  const budgetLine = buckets.map(() => budgetTotal);
  /**
   * ⚠️⚠️ 16 Oct 2026 · **BOTH VIEWS' LINES END ON THE SELECTED EAC'S TILES** — and since EV-10
   * (31 Oct) they follow it the whole way there. The server walks the mode's ETC day by day into
   * the columns (`ahead`): against the allocation, then at the recent burn, so the spend stops
   * rising on the mode's own finish date and runs past the deadline when the mode does. Income
   * steps by each fixed item's agreed sum on its end date and grows with the walked T&M hours, so
   * the margin's last point is ELŐREJELZETT MARGÓ. `forecastLines` is tested.
   */
  const chosen = forecastLines(buckets, eacMode);
  const usedLine = chosen.spend;
  const revenueLine = chosen.income;
  const marginLine = revenueLine.map((value, position) => value - (usedLine[position] ?? 0));
  /* ⚠️ Known income is drawn solid (*"ezt nem kell forecast-elni"*); T&M income still to come is a
     forecast and is dashed like every other one. */
  const incomeForecast = buckets.some((bucket) => Number(bucket.ahead[eacMode].revenue) > 0.004);

  /**
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ 31 Oct 2026 · BEHIND THE LINE, THE RANGE OF THE FIVE (EV-10).   │
   * └──────────────────────────────────────────────────────────────────────┘
   *
   * 19 Oct put the one unselected EAC behind the selected one, faded. With five modes a single
   * faded line only says which other mode the screen happened to pick, so the product owner chose
   * a band: at each column, the lowest to the highest of the ENABLED modes — a disabled mode draws
   * no figure anywhere. Before today every mode is the same measurement, so the band opens where
   * the forecasts part; one mode, or five that agree, draw none.
   */
  const enabled =
    view.eac === null
      ? EAC_MODES.filter((one) => one === 'budget' || one === 'trend')
      : view.eac.modes.filter((one) => one.enabled).map((one) => one.mode);
  const spread = [...new Set<EacMode>([eacMode, ...enabled])].map((one) =>
    forecastLines(buckets, one),
  );
  const rangeOf = (pick: (one: (typeof spread)[number]) => readonly number[]): Range => ({
    low: buckets.map((_, position) => Math.min(...spread.map((one) => pick(one)[position] ?? 0))),
    high: buckets.map((_, position) => Math.max(...spread.map((one) => pick(one)[position] ?? 0))),
  });
  const wide = (range: Range, at: number): boolean =>
    (range.high[at] ?? 0) - (range.low[at] ?? 0) > 0.4;
  const opened = (range: Range): Range | null =>
    range.low.some((_, position) => wide(range, position)) ? range : null;
  const costRange = opened(rangeOf((one) => one.spend));
  const revenueRange = opened(rangeOf((one) => one.income));
  const marginRange = opened(
    rangeOf((one) => one.income.map((value, position) => value - (one.spend[position] ?? 0))),
  );
  const modeTag = (one: EacMode): string => eacLabel(t, one);

  /* ⚠️ U-2 · one hour in money, or `null` and two axes. */
  const rate = view.hourRate === null || Number(view.hourRate) <= 0 ? null : Number(view.hourRate);

  const lines =
    mode === 'budget'
      ? [
          {
            key: 'budget',
            label: t('finance.chart.budget'),
            color: COLOR.budget,
            points: budgetLine,
            range: null,
          },
          {
            key: 'cost',
            label: t('finance.chart.used'),
            color: COLOR.cost,
            points: usedLine,
            range: costRange,
          },
        ]
      : [
          {
            key: 'revenue',
            label: t('finance.chart.revenue'),
            color: COLOR.revenue,
            points: revenueLine,
            range: revenueRange,
          },
          {
            key: 'margin',
            label: t('finance.chart.margin'),
            color: COLOR.margin,
            points: marginLine,
            range: marginRange,
          },
        ];

  /* ⚠️ The band counts toward the axis too, or its top runs off the plot. */
  const plotted = lines.flatMap((line) => [
    ...line.points,
    ...(line.range?.low ?? []),
    ...(line.range?.high ?? []),
  ]);
  /* ⚠️ On one axis the columns are money too, so the tallest of them has to fit as well. */
  const rawTop = Math.max(1, ...plotted, rate === null ? 0 : maxHours * rate) * 1.04;
  /* ⚠️ A negative margin needs room BELOW the baseline, or a loss is drawn as nothing. */
  const rawBottom = Math.min(0, ...plotted) * 1.04;
  /**
   * ⚠️⚠️ 16 Oct 2026 · **WITH A LOSS ON THE CHART, ONE OF THE FIVE TICKS IS ZERO.** They were spread
   * evenly from the bottom, so 0 was never labelled and the floor read *−67,6 e Ft* — which looked
   * like the lines started below zero when they started on it. The axis now gives the negative
   * side whole steps, so the zero line has a label of its own.
   */
  /* ⚠️ How many of the four steps go below zero: whichever of one, two or three needs the SMALLEST
     step, so the axis hugs the data. Rounding the share up instead gave a margin dipping just past
     half the range three steps, and drew a −6 M dip on an axis reaching −20 M (16 Oct 2026). */
  const stepFor = (count: number): number => Math.max(rawTop / (4 - count), -rawBottom / count);
  const below =
    rawBottom < -0.4
      ? [1, 2, 3].reduce((best, count) => (stepFor(count) < stepFor(best) ? count : best), 1)
      : 0;
  const step = below === 0 ? rawTop / 4 : stepFor(below);
  const top = below === 0 ? rawTop : step * (4 - below);
  const bottom = below === 0 ? rawBottom : -step * below;
  const span = top - bottom || 1;
  const y = (value: number): number => 100 - clamp(((value - bottom) / span) * 100);

  /**
   * ⚠️ **A column's height as a share of the plot.** On one axis an hour is `rate` of money,
   * measured from the same baseline as the lines (which sits above the bottom when a loss pushes
   * the money axis below zero). On two, the hours have a ruler of their own.
   */
  const hourTop = maxHours * 1.04;
  const barPct = (value: number): number =>
    clamp(rate === null ? (value / hourTop) * 100 : ((value * rate) / span) * 100);
  const barBottom = rate !== null && bottom < 0 ? ((0 - bottom) / span) * 100 : 0;
  /* The right-hand labels: the money ticks ÷ the rate on one axis, the hour ruler's own otherwise.
     ⚠️ A tick below zero money has no hours to name — a loss is not negative time — so it is blank. */
  const hourTick = (step: number): string => {
    const value = rate === null ? (hourTop * step) / 4 : (bottom + (span * step) / 4) / rate;
    return value < -0.004 ? '' : hours(Math.max(0, value));
  };

  const x = (position: number): number => ((position + 0.5) / count) * 100;
  /**
   * ⚠️ **One series, two polylines**, splitting at `solidTo` and SHARING that point so there is no
   * gap: solid for what happened, `5 3` dashed for what is projected. The mockup's `seriesOf`.
   */
  const segments = (key: string, points: readonly number[], color: string): Segment[] => {
    const at = (position: number): string =>
      `${x(position).toFixed(2)},${y(points[position] ?? 0).toFixed(2)}`;
    const every = points.map((_, position) => position);
    const solid = every.filter((position) => position <= solidTo);
    const dashed = every.filter((position) => position >= solidTo);
    const out: Segment[] = [];
    if (solid.length > 1) {
      out.push({ key: `${key}-s`, points: solid.map(at).join(' '), color, dash: '' });
    }
    if (dashed.length > 1) {
      out.push({ key: `${key}-f`, points: dashed.map(at).join(' '), color, dash: '5 3' });
    }
    /* ⚠️ A lone point is drawn as a MARKER below (`markers`) — a zero-length polyline in a
       stretched viewBox rendered as a speck nobody could read. */
    return out;
  };
  /**
   * ⚠️⚠️ **THE BAND OPENS ON THE LAST COLUMN THE FORECASTS STILL SHARE**, the rule the faded line
   * had (19 Oct): the current period already holds the rest of its own plan, so the modes part
   * inside it. Started at the first column where they differ, the band would float free of the
   * line at a height it never had.
   */
  const bandOf = (
    key: string,
    range: Range,
    color: string,
  ): { key: string; points: string; color: string } | null => {
    const firstApart = range.low.findIndex((_, position) => wide(range, position));
    if (firstApart < 0) return null;
    const shown = buckets
      .map((_, position) => position)
      .filter((position) => position >= Math.max(0, firstApart - 1));
    if (shown.length < 2) return null;
    const at = (position: number, values: readonly number[]): string =>
      `${x(position).toFixed(2)},${y(values[position] ?? 0).toFixed(2)}`;
    return {
      key: `${key}-band`,
      points: [
        ...shown.map((position) => at(position, range.high)),
        ...[...shown].reverse().map((position) => at(position, range.low)),
      ].join(' '),
      color,
    };
  };
  const bands = lines.flatMap((line) => {
    const band = line.range === null ? null : bandOf(line.key, line.range, line.color);
    return band === null ? [] : [band];
  });
  /* ⚠️ The income line is SOLID end to end — *"ezt nem kell forecast-elni mert ezt tudjuk"*: a
     line item's agreed sum on its end date is known, not projected. */
  /**
   * ⚠️⚠️ 17 Oct 2026 · **THE BUDGET IS A RULE ACROSS THE WHOLE PLOT, EDGE TO EDGE.** It never
   * moves, so it was drawn through the column CENTRES like the money lines — half a column short
   * at each end, and a single dot when a line item fits one column. Now it is its own segment
   * from the left edge to the right.
   */
  const drawn: Segment[] = [
    ...lines
      .filter((line) => line.key !== 'budget')
      .flatMap((line) =>
        line.key === 'revenue' && !incomeForecast
          ? segments(line.key, line.points, line.color).map((one) => ({ ...one, dash: '' }))
          : segments(line.key, line.points, line.color),
      ),
    ...lines
      .filter((line) => line.key === 'budget')
      .map((line) => ({
        key: 'budget-rule',
        points: `0,${y(line.points[0] ?? 0).toFixed(2)} 100,${y(line.points[0] ?? 0).toFixed(2)}`,
        color: line.color,
        dash: '',
      })),
  ];
  /* One column: every money line is a single point — a dot with a ring, not a speck. */
  const markers =
    count === 1
      ? lines
          .filter((line) => line.key !== 'budget')
          .map((line) => ({ key: line.key, color: line.color, top: y(line.points[0] ?? 0) }))
      : [];

  const zeroAt = bottom < -0.4 ? ((0 - bottom) / span) * 100 : null;
  /**
   * EV-10 · **THE DEADLINE, WHERE A FORECAST RUNS PAST IT** — spec §2: *a vonal átlépheti a
   * határidőt, és a túllépés a diagramon is látszik*. The columns after the scope's end exist only
   * because a mode's walk reached them, so the rule sits on the edge of the last column inside it.
   */
  const inScope = buckets.filter((bucket) => bucket.from <= view.to).length;
  const deadlineAt = inScope > 0 && inScope < count ? (inScope / count) * 100 : null;
  /* ⚠️ The mockup's widths: 34px a day, 66 a week or a month. */
  const plotWidth = Math.max(480, count * (granularity === 'day' ? 34 : 62));
  const tip = hovered === null ? null : (buckets[hovered] ?? null);
  /* ⚠️ The room on the tooltip's own side caps its width, so a wide one cannot leave the plot. */
  const tipSide =
    hovered === null
      ? {}
      : hovered > count / 2
        ? {
            right: pct(((count - hovered - 0.5) / count) * 100),
            maxWidth: `min(360px, calc(${pct(((hovered + 0.5) / count) * 100)} - 4px))`,
          }
        : {
            left: pct(((hovered + 0.5) / count) * 100),
            maxWidth: `min(360px, calc(${pct(((count - hovered - 0.5) / count) * 100)} - 4px))`,
          };

  const granularities = [
    { value: 'day' as const, label: t('finance.chart.granDay') },
    { value: 'week' as const, label: t('finance.chart.granWeek') },
    { value: 'month' as const, label: t('finance.chart.granMonth') },
  ];

  return (
    <section className="bg-card min-w-0 rounded-xl border shadow-sm">
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-3">
        <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
          {`${mode === 'budget' ? t('finance.chart.titleBudget') : t('finance.chart.titleProfit')} · ${
            cumulative ? t('finance.chart.hoursCum') : t('finance.chart.hoursPeriod')
          }`}
        </span>
        {internal || noBudget ? null : (
          <Switcher
            value={mode}
            options={[
              { value: 'budget', label: t('finance.chart.modeBudget') },
              { value: 'profit', label: t('finance.chart.modeProfit') },
            ]}
            onChange={(value) => {
              setMode(value);
              /* ⚠️ Switching the MODE clears the shape choice, so each view opens in the shape that
                 answers its own question — cumulative for *will the budget last*, per period for
                 *was this month profitable*. */
              setShape(null);
            }}
          />
        )}
        {granularity === null || mobile ? null : (
          <Switcher value={granularity} options={granularities} onChange={onGranularity} />
        )}
        <div className="flex-1" />
        {mobile ? (
          /* 17 Oct 2026 · on a phone the columns and the shape are one ⋯ — the product owner. */
          <DropdownMenu
            width={220}
            align="end"
            label={t('chart.options')}
            items={[
              ...(granularity === null
                ? []
                : [
                    ...granularities.map((option) => ({
                      label: option.label,
                      checked: option.value === granularity,
                      onSelect: () => {
                        onGranularity(option.value);
                      },
                    })),
                    { type: 'separator' as const, label: '' },
                  ]),
              {
                label: t('finance.chart.perPeriod'),
                checked: !cumulative,
                onSelect: () => {
                  setShape('period');
                },
              },
              {
                label: t('finance.chart.cumulative'),
                checked: cumulative,
                onSelect: () => {
                  setShape('cumulative');
                },
              },
            ]}
          >
            <Button variant="ghost" size="icon-sm" aria-label={t('chart.options')}>
              <Icon name="ellipsis" size={15} />
            </Button>
          </DropdownMenu>
        ) : (
          <Switcher
            value={cumulative ? 'cumulative' : 'period'}
            options={[
              { value: 'period', label: t('finance.chart.perPeriod') },
              { value: 'cumulative', label: t('finance.chart.cumulative') },
            ]}
            onChange={setShape}
          />
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="min-w-max px-4 pt-4 pb-3">
          <div className="flex" style={{ height: 240 }}>
            {/* ⚠️ Left axis: MONEY. Sticky, so the ruler survives a horizontal scroll. */}
            <div className="bg-card sticky left-0 z-20 flex w-[92px] shrink-0 flex-col justify-between pr-2 text-right">
              {[4, 3, 2, 1, 0].map((step) => (
                <span
                  key={step}
                  className="text-muted-foreground text-[10.5px] leading-none tabular-nums"
                >
                  {compactMoney(bottom + (span * step) / 4, currency)}
                </span>
              ))}
            </div>

            <div
              className="relative min-w-0 flex-1"
              style={{ minWidth: plotWidth }}
              onMouseLeave={() => {
                setHovered(null);
              }}
            >
              {[0, 25, 50, 75, 100].map((line) => (
                <span
                  key={line}
                  aria-hidden
                  className="border-border/70 absolute inset-x-0 border-t"
                  style={{ bottom: pct(line) }}
                />
              ))}

              {zeroAt === null ? null : (
                <span
                  aria-hidden
                  className="absolute inset-x-0 h-0 border-t"
                  style={{
                    bottom: pct(zeroAt),
                    borderColor: 'color-mix(in oklch, var(--foreground) 35%, transparent)',
                  }}
                />
              )}

              {/**
               * ⚠️⚠️ **WHERE *NOW* IS, WITHOUT WHICH THE CHART IS TWELVE COLUMNS OF NUMBERS.** At
               * the CENTRE of the current bucket, not at the edge of the first future one: the
               * period we are inside is part spent, and a line at its left edge claims the whole
               * of it is still ahead.
               */}
              {currentAt === null ? null : (
                <>
                  <span
                    aria-hidden
                    className="absolute inset-y-0 w-px"
                    style={{
                      left: pct(currentAt),
                      background: 'color-mix(in oklch, var(--foreground) 45%, transparent)',
                    }}
                  />
                  <span
                    className="bg-card text-muted-foreground absolute top-1 z-[5] rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium tracking-wide whitespace-nowrap"
                    style={{ left: pct(currentAt), transform: 'translateX(-50%)' }}
                  >
                    {t('finance.chart.current')}
                  </span>
                </>
              )}

              {deadlineAt === null ? null : (
                <>
                  <span
                    aria-hidden
                    className="absolute inset-y-0 w-0 border-l border-dashed"
                    style={{ left: pct(deadlineAt), borderColor: COLOR.budget }}
                  />
                  <span
                    className="bg-card absolute bottom-1 z-[5] rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium tracking-wide whitespace-nowrap"
                    style={{
                      left: pct(deadlineAt),
                      transform: 'translateX(-50%)',
                      color: COLOR.budget,
                      borderColor: COLOR.budget,
                    }}
                  >
                    {t('finance.chart.deadline')}
                  </span>
                </>
              )}

              {/* ⚠️ The pill that NAMES the flat line, at the height the line sits. */}
              {mode === 'budget' ? (
                <span
                  className="bg-card absolute z-[6] rounded-full border px-1.5 py-0.5 text-[9.5px] font-medium tracking-wide whitespace-nowrap"
                  style={{
                    bottom: pct(Math.max(2, Math.min(98, 100 - y(budgetTotal)))),
                    left: 4,
                    transform: 'translateY(50%)',
                    color: COLOR.budget,
                    borderColor: COLOR.budget,
                  }}
                >
                  {t('finance.chart.budget')}
                </span>
              ) : null}

              <div className="absolute inset-0 flex items-end gap-1">
                {buckets.map((bucket, position) => (
                  <button
                    key={bucket.key}
                    type="button"
                    className="relative h-full min-w-0 flex-1 cursor-default"
                    aria-label={bucketLabel(bucket, granularity, locale)}
                    onMouseEnter={() => {
                      setHovered(position);
                    }}
                    onFocus={() => {
                      setHovered(position);
                    }}
                    onBlur={() => {
                      setHovered(null);
                    }}
                  >
                    {bucket.future ? (
                      <span aria-hidden className="bg-muted/25 absolute inset-0" />
                    ) : null}
                    {/* ⚠️⚠️ **A FORECAST BAR IS HATCHED, WHICH THE EFFORT CHART HAS ALWAYS DONE
                        AND THIS ONE DID NOT.** The shaded block behind the future buckets says
                        *these are later*; it does not say the BARS in them are projections rather
                        than measurements. Two charts on two tabs of one screen drawing the same
                        distinction two ways is the thing to avoid. */}
                    {/* ⚠️ PLANNED is the wide one BEHIND; LOGGED the narrow one in FRONT. */}
                    <span
                      aria-hidden
                      /* ⚠️ Capped, so a lone column does not paint a bar across the whole plot. */
                      className="absolute inset-x-[10%] mx-auto max-w-[44px] rounded-t-[3px]"
                      style={{
                        bottom: pct(barBottom),
                        height: pct(barPct(plannedBars[position] ?? 0)),
                        background: bucket.future ? HATCH_PLANNED : COLOR.planned,
                      }}
                    />
                    {/* ⚠️ The mode's walked hours: the whole narrow column ahead, and the part of
                        the current one still to come, hatched behind its logged part. */}
                    {bucket.future || walkedIn[position] === true ? (
                      <span
                        aria-hidden
                        className="absolute inset-x-[27%] mx-auto max-w-[22px] rounded-t-[3px]"
                        style={{
                          bottom: pct(barBottom),
                          height: pct(barPct(forecastBars[position] ?? 0)),
                          background: HATCH_LOGGED,
                        }}
                      />
                    ) : null}
                    {bucket.future ? null : (
                      <span
                        aria-hidden
                        className="absolute inset-x-[27%] mx-auto max-w-[22px] rounded-t-[3px]"
                        style={{
                          bottom: pct(barBottom),
                          height: pct(barPct(loggedBars[position] ?? 0)),
                          background: COLOR.logged,
                        }}
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* ⚠️ 17 Oct 2026 · CLIPPED to the plot: drawn `overflow-visible`, the forecast's last
                  stretch ran out past the plot and over the hours axis on a phone. */}
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="pointer-events-none absolute inset-0 size-full overflow-hidden"
                aria-hidden
              >
                {bands.map((band) => (
                  <polygon
                    key={band.key}
                    points={band.points}
                    fill={band.color}
                    fillOpacity={0.14}
                    stroke="none"
                  />
                ))}
                {drawn.map((segment) => (
                  <polyline
                    key={segment.key}
                    points={segment.points}
                    fill="none"
                    stroke={segment.color}
                    strokeWidth={2}
                    strokeDasharray={segment.dash}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                ))}
              </svg>

              {markers.map((marker) => (
                <span
                  key={marker.key}
                  aria-hidden
                  className="bg-card pointer-events-none absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2"
                  style={{ left: '50%', top: pct(marker.top), borderColor: marker.color }}
                />
              ))}

              {tip === null || hovered === null ? null : (
                /* ⚠️⚠️ 2 Nov 2026 · **AS WIDE AS ITS LONGEST ROW, NOT A FIXED 210px.** A mode's
                   name after FORECAST and a range of two full sums do not fit 210px: the label
                   wrapped to three lines and the range ran over it. It grows to 360px, and never
                   past the room on its own side of the column it is anchored to. */
                <div
                  ref={tipRef}
                  className="bg-popover text-popover-foreground pointer-events-none absolute z-10 w-max min-w-[210px] rounded-lg border p-2.5 shadow-md"
                  style={{
                    top: 4,
                    ...tipSide,
                    transform: `translate(${String(nudge.x)}px, ${String(nudge.y)}px)`,
                  }}
                >
                  <div className="text-[11.5px] font-medium tabular-nums">
                    {`${bucketLabel(tip, granularity, locale)} · ${tip.from} – ${tip.to}`}
                  </div>
                  {tip.future ? null : (
                    <Row
                      color={COLOR.logged}
                      label={t('finance.chart.loggedHours')}
                      value={hours(loggedBars[hovered] ?? 0)}
                    />
                  )}
                  {tip.future || walkedIn[hovered] === true ? (
                    <Row
                      color={COLOR.logged}
                      faded
                      label={`${t('finance.chart.forecastHours')} · ${modeTag(eacMode)}`}
                      value={hours(forecastBars[hovered] ?? 0)}
                    />
                  ) : null}
                  <Row
                    color={COLOR.planned}
                    label={t('finance.chart.plannedHours')}
                    value={hours(plannedBars[hovered] ?? 0)}
                  />
                  {mode === 'budget' ? (
                    <>
                      <Row
                        color={COLOR.budget}
                        label={t('finance.chart.budget')}
                        value={money(budgetTotal, currency)}
                      />
                      <Row
                        color={COLOR.cost}
                        label={
                          costRange !== null && wide(costRange, hovered)
                            ? `${t('finance.chart.forecast')} · ${modeTag(eacMode)}`
                            : hovered <= solidTo
                              ? t('finance.chart.used')
                              : t('finance.chart.forecast')
                        }
                        value={money(usedLine[hovered] ?? 0, currency)}
                        over={(usedLine[hovered] ?? 0) > budgetTotal + 0.4}
                      />
                      {costRange !== null && wide(costRange, hovered) ? (
                        <Row
                          color={COLOR.cost}
                          faded
                          label={t('finance.chart.range')}
                          value={`${money(costRange.low[hovered] ?? 0, currency)} – ${money(costRange.high[hovered] ?? 0, currency)}`}
                        />
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Row
                        color={COLOR.revenue}
                        label={
                          revenueRange !== null && wide(revenueRange, hovered)
                            ? `${t('finance.chart.revenue')} · ${modeTag(eacMode)}`
                            : t('finance.chart.revenue')
                        }
                        value={money(revenueLine[hovered] ?? 0, currency)}
                      />
                      {revenueRange !== null && wide(revenueRange, hovered) ? (
                        <Row
                          color={COLOR.revenue}
                          faded
                          label={t('finance.chart.range')}
                          value={`${money(revenueRange.low[hovered] ?? 0, currency)} – ${money(revenueRange.high[hovered] ?? 0, currency)}`}
                        />
                      ) : null}
                      <Row
                        color={COLOR.margin}
                        label={
                          marginRange !== null && wide(marginRange, hovered)
                            ? `${t('finance.chart.margin')} · ${modeTag(eacMode)}`
                            : t('finance.chart.margin')
                        }
                        value={signedMoney(marginLine[hovered] ?? 0, currency)}
                        over={(marginLine[hovered] ?? 0) < -0.4}
                      />
                      {marginRange !== null && wide(marginRange, hovered) ? (
                        <Row
                          color={COLOR.margin}
                          faded
                          label={t('finance.chart.range')}
                          value={`${signedMoney(marginRange.low[hovered] ?? 0, currency)} – ${signedMoney(marginRange.high[hovered] ?? 0, currency)}`}
                        />
                      ) : null}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ⚠️ Right axis: HOURS — on one axis the money ticks ÷ the hour rate, so each label
                sits level with the money label it equals. */}
            <div className="bg-card sticky right-0 z-20 flex w-[58px] shrink-0 flex-col justify-between pl-2 text-left">
              {[4, 3, 2, 1, 0].map((step) => (
                <span
                  key={step}
                  className="text-muted-foreground text-[10.5px] leading-none tabular-nums"
                >
                  {hourTick(step)}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-2 flex">
            <div className="bg-card sticky left-0 z-20 w-[92px] shrink-0" />
            <div className="flex min-w-0 flex-1 gap-1" style={{ minWidth: plotWidth }}>
              {buckets.map((bucket, position) => (
                /* ⚠️ 3 Nov 2026 · overflows its column centred rather than truncating to "okt…" —
                   the first of a month ("okt. 1.") is a few px wider than a 34px day column. */
                <span key={bucket.key} className="flex min-w-0 flex-1 justify-center">
                  <span
                    className={`text-[10px] whitespace-nowrap tabular-nums ${tick(bucket, position, solidTo)}`}
                  >
                    {bucketLabel(bucket, granularity, locale)}
                  </span>
                </span>
              ))}
            </div>
            <div className="bg-card sticky right-0 z-20 w-[58px] shrink-0" />
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * 16 Oct 2026 · a column's label. A day is its number — and ⚠️ **the first of a month carries the
 * month's short name** (*okt. 1.*), the product owner's one change to the mockup, so a reader
 * scrolling days knows which month they are in. A week is its Monday; a month and a period keep
 * the server's label. Dates are the reader's locale's words, not ours (rule 7).
 */
function bucketLabel(
  bucket: FinanceBucket,
  granularity: FinanceGranularity | null,
  locale: Locale,
): string {
  if (granularity !== 'day' && granularity !== 'week') return bucket.label;
  const [year, month, day] = bucket.from.split('-').map(Number);
  const date = new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
  if (granularity === 'week' || day === 1) {
    return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(locale, { day: 'numeric' });
}

/** ⚠️ The current bucket is the one the reader is standing in, so it is the one drawn in weight. */
function tick(bucket: FinanceBucket, position: number, solidTo: number): string {
  if (position === solidTo && !bucket.future) return 'text-foreground font-medium';
  return bucket.future ? 'text-muted-foreground/60' : 'text-muted-foreground';
}

function Row({
  color,
  label,
  value,
  over = false,
  faded = false,
}: {
  readonly color: string;
  readonly label: string;
  readonly value: string;
  readonly over?: boolean;
  /** EV-10 · the band's row — a hollow dot and quieter figures, as the band is quieter. */
  readonly faded?: boolean;
}) {
  return (
    /* ⚠️ 2 Nov 2026 · neither half wraps, and the row does: where a label and its figure do not fit
       side by side, the figure drops to its own line, still right-aligned, rather than the label
       breaking into three lines or the figure running over it. */
    <div className={`mt-1 flex flex-wrap items-center gap-x-2 ${faded ? 'opacity-60' : ''}`}>
      <span className="flex items-center gap-2">
        <span
          aria-hidden
          className="size-2 shrink-0 rounded-full"
          style={faded ? { border: `1.5px solid ${color}` } : { background: color }}
        />
        <span className="text-muted-foreground text-[10.5px] font-medium tracking-wide whitespace-nowrap uppercase">
          {label}
        </span>
      </span>
      <span
        className={`ml-auto text-[11px] whitespace-nowrap tabular-nums ${over ? 'text-destructive' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

/** ⚠️ 16 Oct 2026 · the kit's `Segmented` now — this was its first private copy. */
function Switcher<T extends string>(props: {
  readonly value: T;
  readonly options: readonly { value: T; label: string }[];
  readonly onChange: (value: T) => void;
}) {
  return <Segmented {...props} />;
}
